import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { geoguesserService } from '../../../services/api';
import MapPinSelector from '../../MapPinSelector';
import './BulkLevelImportWizard.css';

const MAX_FILES = 50;
const CONCURRENCY = 3;

const STEPS = [
  { id: 1, label: 'Upload' },
  { id: 2, label: 'Details' },
  { id: 3, label: 'Answers' },
  { id: 4, label: 'Import' },
];

function errorDetail(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'object' && data) {
    const entries = Object.entries(data);
    if (entries.length > 0) {
      return entries
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('; ');
    }
  }
  return data?.detail || fallback;
}

function nameFromFilename(file) {
  const base = file.name.replace(/\.[^.]+$/i, '').replace(/[_-]+/g, ' ').trim();
  return base || 'Untitled level';
}

/** Ensure names are unique against each other and any already-used titles. */
function dedupeNamesAgainstUsed(baseNames, used) {
  const result = [];
  for (const base of baseNames) {
    let candidate = base;
    let n = 1;
    while (used.has(candidate)) {
      n += 1;
      candidate = `${base} (${n})`;
    }
    used.add(candidate);
    result.push(candidate);
  }
  return result;
}

function makeRowId() {
  return `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Bulk level import: multi-file → name/floor table → pin stepper → sequential POSTs.
 */
const BulkLevelImportWizard = ({
  mapId,
  floors = [],
  floorBuffers = {},
  pickFloorImage,
  onClose,
  onComplete,
  onError,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);
  const rowsLenRef = useRef(0);
  rowsLenRef.current = rows.length;
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [namePrefix, setNamePrefix] = useState('');
  const [nameSuffix, setNameSuffix] = useState('');
  const [applyFloorId, setApplyFloorId] = useState('');
  const [applyDifficulty, setApplyDifficulty] = useState('medium');
  const [pinIndex, setPinIndex] = useState(0);
  /** Keep in sync so MapPinSelector's onPinChange never uses a stale pin index. */
  const pinIndexRef = useRef(0);
  pinIndexRef.current = pinIndex;
  const [committing, setCommitting] = useState(false);
  const [commitDone, setCommitDone] = useState(0);
  const [commitStatuses, setCommitStatuses] = useState({});
  const [commitErrors, setCommitErrors] = useState({});
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  /** Full-screen preview of a level photo (upload step, table, or pin step). */
  const [photoLightbox, setPhotoLightbox] = useState(null);

  const floorById = useMemo(() => {
    const m = {};
    for (const f of floors) m[f.id] = f;
    return m;
  }, [floors]);

  const reportError = useCallback(
    (msg) => {
      if (typeof onError === 'function') onError(msg);
    },
    [onError],
  );
  const reportSuccess = useCallback(
    (msg) => {
      if (typeof onSuccess === 'function') onSuccess(msg);
    },
    [onSuccess],
  );

  useEffect(() => {
    if (!photoLightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPhotoLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoLightbox]);

  const openPhotoLightbox = useCallback((src, label) => {
    if (!src) return;
    setPhotoLightbox({ src, alt: label || 'Level photo' });
  }, []);

  const resetWizard = useCallback(() => {
    setRows((prev) => {
      prev.forEach((r) => {
        if (r.previewUrl) URL.revokeObjectURL(r.previewUrl);
      });
      return [];
    });
    setStep(1);
    setPinIndex(0);
    setCommitting(false);
    setCommitDone(0);
    setCommitStatuses({});
    setCommitErrors({});
    setShowErrorDetails(false);
    setPhotoLightbox(null);
  }, []);

  const handleClose = useCallback(() => {
    resetWizard();
    if (typeof onClose === 'function') onClose();
  }, [onClose, resetWizard]);

  const addFiles = useCallback(
    (fileList) => {
      const arr = Array.from(fileList || []).filter((f) =>
        /^image\//i.test(f.type),
      );
      if (arr.length === 0) {
        reportError('Choose image files only.');
        return;
      }
      if (rowsLenRef.current + arr.length > MAX_FILES) {
        reportError(`At most ${MAX_FILES} images per batch.`);
        return;
      }
      setRows((prev) => {
        const used = new Set(
          prev.map((r) => (r.name || '').trim()).filter(Boolean),
        );
        const baseNames = arr.map(nameFromFilename);
        const names = dedupeNamesAgainstUsed(baseNames, used);
        const defaultFloor = floors[0]?.id ?? '';
        const next = arr.map((file, i) => ({
          id: makeRowId(),
          file,
          previewUrl: URL.createObjectURL(file),
          name: names[i],
          floorId: defaultFloor,
          difficulty: 'medium',
          answer_x: null,
          answer_y: null,
          rowErrors: {},
        }));
        return [...prev, ...next];
      });
      reportSuccess(`Added ${arr.length} image${arr.length === 1 ? '' : 's'}.`);
    },
    [floors, reportError, reportSuccess],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const validateStep2 = () => {
    let ok = true;
    setRows((prev) =>
      prev.map((r) => {
        const rowErrors = {};
        if (!(r.name || '').trim()) rowErrors.name = 'Required';
        if (!r.floorId) rowErrors.floor = 'Required';
        if (Object.keys(rowErrors).length) ok = false;
        return { ...r, rowErrors };
      }),
    );
    return ok;
  };

  const validateStep3 = () => {
    const noFloor = rows.filter((r) => !r.floorId);
    if (noFloor.length > 0) {
      reportError(
        `Choose a floor for every level (${noFloor.length} missing).`,
      );
      return false;
    }
    const bad = rows.filter(
      (r) => r.answer_x == null || r.answer_y == null,
    );
    if (bad.length > 0) {
      reportError(
        `Place an answer for every level (${bad.length} missing).`,
      );
      return false;
    }
    return true;
  };

  const applyPrefixSuffix = () => {
    const pre = (namePrefix || '').trim();
    const suf = (nameSuffix || '').trim();
    if (!pre && !suf) return;
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        name: `${pre}${r.name}${suf}`.trim(),
        rowErrors: {},
      })),
    );
    reportSuccess('Names updated.');
  };

  const applyFloorToAll = () => {
    if (!applyFloorId) return;
    const fid = Number(applyFloorId);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        floorId: fid,
        rowErrors: { ...r.rowErrors, floor: undefined },
      })),
    );
  };

  const applyDifficultyToAll = () => {
    setRows((prev) =>
      prev.map((r) => ({ ...r, difficulty: applyDifficulty })),
    );
  };

  const goNext = () => {
    if (step === 1) {
      if (rows.length === 0) {
        reportError('Add at least one image.');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!validateStep2()) {
        reportError('Fix row errors (name and floor required).');
        return;
      }
      setPinIndex(0);
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!validateStep3()) return;
      setStep(4);
      runCommit();
    }
  };

  const goBack = () => {
    if (step <= 1) return;
    if (step === 4 && committing) return;
    setStep((s) => Math.max(1, s - 1));
  };

  const runCommit = async () => {
    setCommitting(true);
    setCommitDone(0);
    const statuses = {};
    const errors = {};
    rows.forEach((r) => {
      statuses[r.id] = 'pending';
    });
    setCommitStatuses({ ...statuses });

    const queue = [...rows];
    let completed = 0;

    const worker = async () => {
      while (queue.length > 0) {
        const row = queue.shift();
        if (!row) break;
        setCommitStatuses((prev) => ({ ...prev, [row.id]: 'uploading' }));
        const fd = new FormData();
        fd.append('geogusser_map', String(mapId));
        fd.append('floor', String(row.floorId));
        fd.append('name', (row.name || '').trim());
        fd.append('difficulty', row.difficulty || 'medium');
        fd.append('hint', '');
        fd.append('answer_x', String(row.answer_x));
        fd.append('answer_y', String(row.answer_y));
        fd.append('photo', row.file);
        try {
          await geoguesserService.createLevel(fd);
          completed += 1;
          setCommitDone(completed);
          setCommitStatuses((prev) => ({ ...prev, [row.id]: 'ok' }));
        } catch (err) {
          completed += 1;
          setCommitDone(completed);
          errors[row.id] = errorDetail(err, 'Create failed');
          setCommitErrors((prev) => ({
            ...prev,
            [row.id]: errors[row.id],
          }));
          setCommitStatuses((prev) => ({ ...prev, [row.id]: 'err' }));
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, rows.length) }, () =>
        worker(),
      ),
    );

    setCommitting(false);
    const failCount = Object.keys(errors).length;
    if (failCount === 0) {
      reportSuccess(`Created ${rows.length} level${rows.length === 1 ? '' : 's'}.`);
      if (typeof onComplete === 'function') onComplete();
      resetWizard();
      if (typeof onClose === 'function') onClose();
    } else {
      reportError(
        `Created ${rows.length - failCount} of ${rows.length}; ${failCount} failed.`,
      );
    }
  };

  const currentPinRow = rows[pinIndex];
  const currentFloor = currentPinRow
    ? floorById[currentPinRow.floorId]
    : null;
  const floorImg = currentFloor
    ? pickFloorImage(currentFloor)
    : null;

  const setPinStepFloor = useCallback((floorId) => {
    const fid = floorId ? Number(floorId) : '';
    const idx = pinIndexRef.current;
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const prevFloor = row.floorId;
        const next = {
          ...row,
          floorId: fid,
        };
        if (fid !== prevFloor) {
          next.answer_x = null;
          next.answer_y = null;
        }
        return next;
      }),
    );
  }, []);

  const noFloors = !floors || floors.length === 0;

  if (noFloors) {
    return (
      <div className="bulk-level-wizard bulk-level-wizard--empty">
        <p className="bulk-level-wizard-muted">
          Add at least one floor to this map before importing levels.
        </p>
        <button type="button" className="btn-secondary" onClick={handleClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="bulk-level-wizard">
      <div className="bulk-level-wizard-head">
        <div>
          <h4 className="bulk-level-wizard-title">Add multiple levels</h4>
          <p className="bulk-level-wizard-sub">
            Upload photos, set names and floors, place each answer, then import.
          </p>
        </div>
        <button
          type="button"
          className="close-btn bulk-level-wizard-close"
          onClick={handleClose}
          aria-label="Close bulk import"
        >
          ×
        </button>
      </div>

      <nav className="bulk-level-steps" aria-label="Import steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && <span className="bulk-level-steps-sep" aria-hidden />}
            <span
              className={`bulk-level-step ${step === s.id ? 'is-current' : ''} ${step > s.id ? 'is-done' : ''}`}
              aria-current={step === s.id ? 'step' : undefined}
            >
              <span className="bulk-level-step-num">{s.id}</span>
              {s.label}
            </span>
          </React.Fragment>
        ))}
      </nav>

      {step === 1 && (
        <div className="bulk-level-panel">
          <div
            className={`bulk-level-dropzone ${dragOver ? 'is-dragover' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <span className="material-symbols-outlined bulk-level-dropzone-icon">
              upload_file
            </span>
            <p className="bulk-level-dropzone-title">
              Drop level photos here or{' '}
              <button
                type="button"
                className="bulk-level-link-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
            </p>
            <p className="bulk-level-dropzone-hint">
              Up to {MAX_FILES} images · JPEG, PNG, WebP…
            </p>
            <input
              ref={fileInputRef}
              type="file"
              className="bulk-level-file-input"
              accept="image/*"
              multiple
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
          {rows.length > 0 && (
            <div className="bulk-level-thumb-strip">
              <span className="bulk-level-badge">{rows.length} images</span>
              <div className="bulk-level-thumb-row">
                {rows.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="bulk-level-thumb-btn"
                    onClick={() => openPhotoLightbox(r.previewUrl, r.name)}
                    aria-label={`Enlarge preview: ${r.name}`}
                  >
                    <img src={r.previewUrl} alt="" className="bulk-level-thumb" />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => {
                  rows.forEach((r) => {
                    if (r.previewUrl) URL.revokeObjectURL(r.previewUrl);
                  });
                  setRows([]);
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="bulk-level-panel">
          <div className="bulk-level-toolbar">
            <div className="bulk-level-toolbar-group">
              <label className="bulk-level-toolbar-label">Prefix</label>
              <input
                type="text"
                className="bulk-level-input"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="e.g. Oregon — "
              />
              <label className="bulk-level-toolbar-label">Suffix</label>
              <input
                type="text"
                className="bulk-level-input"
                value={nameSuffix}
                onChange={(e) => setNameSuffix(e.target.value)}
                placeholder=" optional"
              />
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={applyPrefixSuffix}
              >
                Apply to names
              </button>
            </div>
            <div className="bulk-level-toolbar-group">
              <label className="bulk-level-toolbar-label">Floor for all</label>
              <select
                className="bulk-level-select"
                value={applyFloorId}
                onChange={(e) => setApplyFloorId(e.target.value)}
              >
                <option value="">Select…</option>
                {floors.map((f) => {
                  const live =
                    (floorBuffers[f.id]?.name || '').trim() || f.name;
                  return (
                    <option key={f.id} value={f.id}>
                      {live}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={applyFloorToAll}
                disabled={!applyFloorId}
              >
                Apply floor
              </button>
              <label className="bulk-level-toolbar-label">Difficulty</label>
              <select
                className="bulk-level-select"
                value={applyDifficulty}
                onChange={(e) => setApplyDifficulty(e.target.value)}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={applyDifficultyToAll}
              >
                Apply difficulty
              </button>
            </div>
          </div>
          <div className="bulk-level-table-wrap">
            <table className="bulk-level-table">
              <thead>
                <tr>
                  <th className="bulk-level-th-thumb" />
                  <th>Name</th>
                  <th>Floor</th>
                  <th>Difficulty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      r.rowErrors?.name || r.rowErrors?.floor ? 'has-error' : ''
                    }
                  >
                    <td>
                      <button
                        type="button"
                        className="bulk-level-table-thumb-btn"
                        onClick={() => openPhotoLightbox(r.previewUrl, r.name)}
                        aria-label={`Enlarge: ${r.name}`}
                      >
                        <img
                          src={r.previewUrl}
                          alt=""
                          className="bulk-level-table-thumb"
                        />
                      </button>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="bulk-level-input bulk-level-input-table"
                        value={r.name}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? {
                                    ...x,
                                    name: e.target.value,
                                    rowErrors: {
                                      ...x.rowErrors,
                                      name: undefined,
                                    },
                                  }
                                : x,
                            ),
                          )
                        }
                      />
                      {r.rowErrors?.name && (
                        <div className="bulk-level-field-error" role="alert">
                          {r.rowErrors.name}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="bulk-level-select bulk-level-select-table"
                        value={r.floorId || ''}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? {
                                    ...x,
                                    floorId: e.target.value
                                      ? Number(e.target.value)
                                      : '',
                                    rowErrors: {
                                      ...x.rowErrors,
                                      floor: undefined,
                                    },
                                  }
                                : x,
                            ),
                          )
                        }
                      >
                        <option value="">Select…</option>
                        {floors.map((f) => {
                          const live =
                            (floorBuffers[f.id]?.name || '').trim() || f.name;
                          return (
                            <option key={f.id} value={f.id}>
                              {live}
                            </option>
                          );
                        })}
                      </select>
                      {r.rowErrors?.floor && (
                        <div className="bulk-level-field-error" role="alert">
                          {r.rowErrors.floor}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="bulk-level-select bulk-level-select-table"
                        value={r.difficulty}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, difficulty: e.target.value }
                                : x,
                            ),
                          )
                        }
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 3 && currentPinRow && (
        <div className="bulk-level-panel bulk-level-pin-panel">
          <div className="bulk-level-pin-head">
            <div>
              <p className="bulk-level-pin-progress">
                Level {pinIndex + 1} of {rows.length}
              </p>
              <h5 className="bulk-level-pin-name">{currentPinRow.name}</h5>
            </div>
            <div className="bulk-level-pin-photo-block">
              <button
                type="button"
                className="bulk-level-pin-level-thumb-btn"
                onClick={() =>
                  openPhotoLightbox(
                    currentPinRow.previewUrl,
                    currentPinRow.name,
                  )
                }
                aria-label={`Enlarge level photo: ${currentPinRow.name}`}
              >
                <img
                  src={currentPinRow.previewUrl}
                  alt=""
                  className="bulk-level-pin-level-thumb"
                />
              </button>
              <span className="bulk-level-pin-photo-hint">Tap to enlarge</span>
            </div>
          </div>

          <div className="bulk-level-pin-toolbar">
            <div className="bulk-level-pin-toolbar-row">
              <label
                className="bulk-level-toolbar-label"
                htmlFor="bulk-pin-floor-select"
              >
                Floor for this level
              </label>
              <select
                id="bulk-pin-floor-select"
                className="bulk-level-select bulk-level-pin-floor-select"
                value={currentPinRow.floorId || ''}
                onChange={(e) => setPinStepFloor(e.target.value)}
              >
                <option value="">Select…</option>
                {floors.map((f) => {
                  const live =
                    (floorBuffers[f.id]?.name || '').trim() || f.name;
                  return (
                    <option key={f.id} value={f.id}>
                      {live}
                    </option>
                  );
                })}
              </select>
            </div>
            <p className="bulk-level-pin-toolbar-hint">
              Change the floor if the answer belongs on another map image. The pin
              is cleared when the floor changes so coordinates stay accurate.
            </p>
          </div>

          <div className="bulk-level-pin-bar">
            {rows.map((r, i) => (
              <button
                key={r.id}
                type="button"
                className={`bulk-level-pin-dot ${i === pinIndex ? 'is-active' : ''} ${r.answer_x != null ? 'is-done' : ''}`}
                onClick={() => setPinIndex(i)}
                aria-label={`Level ${i + 1}`}
              />
            ))}
          </div>
          <div className="bulk-level-pin-step-nav">
            <button
              type="button"
              className="btn-secondary"
              disabled={pinIndex <= 0}
              onClick={() => setPinIndex((i) => Math.max(0, i - 1))}
            >
              Previous level
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={pinIndex >= rows.length - 1}
              onClick={() =>
                setPinIndex((i) => Math.min(rows.length - 1, i + 1))
              }
            >
              Next level
            </button>
          </div>
          {floorImg ? (
            <div className="bulk-level-pin-stage">
              <MapPinSelector
                mapImage={floorImg}
                pinPosition={
                  currentPinRow.answer_x != null
                    ? { x: currentPinRow.answer_x, y: currentPinRow.answer_y }
                    : null
                }
                onPinChange={(x, y) => {
                  const idx = pinIndexRef.current;
                  setRows((prev) =>
                    prev.map((xrow, i) =>
                      i === idx ? { ...xrow, answer_x: x, answer_y: y } : xrow,
                    ),
                  );
                }}
                enableZoomPan
                showZoomControls
                mode="admin"
              />
            </div>
          ) : (
            <p className="bulk-level-wizard-muted bulk-level-pin-map-missing" role="alert">
              This floor has no image yet. Pick another floor above, or go back
              to assign a floor image first.
            </p>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="bulk-level-panel">
          <p className="bulk-level-commit-summary">
            {committing
              ? `Creating… ${commitDone} / ${rows.length}`
              : `Finished: ${rows.filter((r) => commitStatuses[r.id] === 'ok').length} ok, ${rows.filter((r) => commitStatuses[r.id] === 'err').length} failed`}
          </p>
          <div
            className="bulk-level-progress-track"
            role="progressbar"
            aria-valuenow={commitDone}
            aria-valuemin={0}
            aria-valuemax={rows.length}
          >
            <div
              className="bulk-level-progress-fill"
              style={{
                width: `${rows.length ? (commitDone / rows.length) * 100 : 0}%`,
              }}
            />
          </div>
          <ul className="bulk-level-commit-list">
            {rows.map((r) => {
              const st = commitStatuses[r.id] || 'pending';
              return (
                <li key={r.id} className={`bulk-level-commit-row is-${st}`}>
                  <span className="bulk-level-commit-status" />
                  <span className="bulk-level-commit-name">{r.name}</span>
                  {st === 'err' && commitErrors[r.id] && (
                    <span className="bulk-level-commit-err">{commitErrors[r.id]}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {Object.keys(commitErrors).length > 0 && !committing && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setShowErrorDetails((v) => !v)}
            >
              {showErrorDetails ? 'Hide' : 'Show'} error details
            </button>
          )}
          {showErrorDetails && (
            <ul className="bulk-level-error-list" role="alert">
              {rows
                .filter((r) => commitErrors[r.id])
                .map((r) => (
                  <li key={r.id}>
                    <strong>{r.name}:</strong> {commitErrors[r.id]}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {photoLightbox && (
        <div
          className="bulk-level-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={photoLightbox.alt}
          onClick={() => setPhotoLightbox(null)}
        >
          <button
            type="button"
            className="bulk-level-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setPhotoLightbox(null);
            }}
            aria-label="Close enlarged photo"
          >
            ×
          </button>
          <div
            className="bulk-level-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoLightbox.src}
              alt={photoLightbox.alt}
              className="bulk-level-lightbox-img"
            />
          </div>
        </div>
      )}

      <div className="bulk-level-footer">
        <button
          type="button"
          className="btn-secondary"
          onClick={goBack}
          disabled={step === 1 || (step === 4 && committing)}
        >
          Back
        </button>
        {step < 4 && (
          <button type="button" className="btn-primary" onClick={goNext}>
            {step === 3 ? 'Start import' : 'Continue'}
          </button>
        )}
        {step === 4 && !committing && Object.keys(commitErrors).length > 0 && (
          <button type="button" className="btn-primary" onClick={handleClose}>
            Done
          </button>
        )}
      </div>
    </div>
  );
};

export default BulkLevelImportWizard;
